import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Box, Button, Autocomplete, TextField, Typography, CircularProgress } from "@mui/material";
import FormHeader from "../../../components/dms_common/FormHeader";
import "../../../components/dms_common/FormTextField.css";
import "../../../components/dms_common/Search.css";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BatchComponent from "../manualStockAdjustment/BatchComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { ConfirmationPopUp } from "../../../components/popup/confirmationPopUp";
import { GenericPopUp } from "../../../components/popup/genericPopUp";
import Search from "../../../components/dms_common/Search";
import { BatchAndExpiryManagementRowsData, popupType, BatchWiseStockApiResponse, TransformedBatch } from "../../../types/types";
import { stockTypeMapping } from "../manualStockAdjustment/ManualStockAdjustmentUtils";
import { transformToBatchwiseRowsData } from "../batchAndExpiryManagement/BatchAndExpiryManagementUtils";
import { ChannelkartNetworkPut, ChannelkartNetworkGet } from "../../../services/networkService";
import "./NewStockAndUpdateForm.css";
import { customStyle, GenericModal, getGenericModalProps, initialGenericModalProps } from "../../viewOrder/ViewOrderUtils";
import CustomGenericModal from "../../../components/dms_common/CustomGenericModal";
import ScreenOverlay from "../../../components/dms_common/ScreenOverlay";
import { EnvironmentRegistry } from "../../../services/registry/environmentRegistry";
import useShortcutKeys from "../../../components/dms_common/ShortcutKeys";
import { getDataFromLocalDb } from "../../../services/dbService";
import { Product } from "../../createSecondaryOrder/models";

const currentStockTypeOptions = ["Available"];

const customMuiInputStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#EAEAEA",
    },
    "&:hover fieldset": {
      borderColor: "#B9B9B9",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1C7EA0",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#B5B5B5",
    fontSize: "13px",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#1C7EA0",
  },
};

interface batchWiseDataType {
  [batchId: string]: {
    quantityCs: string;
    quantityPc: string;
    quantityOther: string;
    newQuantityCs: string;
    newQuantityPc: string;
    newQuantityOther: string;
    reason: string;
    updatedType: string;
  };
}

export interface StockTypeUpdateFormData {
  skuName: string;
  skuCode: string;
  batchId: string;
  stockType: string;
  batchWiseData: batchWiseDataType;
}

const initialFormData = {
  skuName: "",
  skuCode: "",
  batchId: "",
  quantityCs: "0",
  quantityPc: "0",
  quantityOther: "0",
  newQuantityCs: "",
  newQuantityPc: "",
  newQuantityOther: "",
  stockType: "Available",
  batchWiseData: {},
};

interface CreateBatchIdFormProps {
  setOpenNewStockTypeUpdateForm: Dispatch<SetStateAction<boolean>>;
  skuProductDescriptionMap: Record<string, string>;
  skuUomMap: Record<string, string>;
  selectedDistributor: string;
  isCompanyUser: boolean;
}

const CreateNewStockTypeUpdateForm: React.FC<CreateBatchIdFormProps> = ({
  setOpenNewStockTypeUpdateForm,
  skuProductDescriptionMap,
  skuUomMap,
  selectedDistributor,
  isCompanyUser,
}) => {
  const [confirmPopupState, setConfirmPopupState] = useState<boolean>(false);
  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [genericMessage, setGenericMessage] = useState<string>("");
  const [formData, setFormData] = useState<StockTypeUpdateFormData>(initialFormData);
  const [isApplyDisabled, setIsApplyDisabled] = useState(true);
  const [allBatchIds, setAllBatchIds] = useState<batchWiseDataType>({});
  const [batchIds, setBatchIds] = useState<string[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);
  const [mandatoryFieldError, setMandatoryFieldError] = useState<boolean>(false);
  const existingBatchIdsRef = useRef<BatchAndExpiryManagementRowsData[]>([]);
  const [genericModalProps, setGenericModalProps] = useState<GenericModal>(
    initialGenericModalProps
  );
  const [searchValue,setSearchValue]=useState<string>('')
  const [applyLoading, setApplyLoading] = useState<boolean>(false)
  const { positiveCta } = useShortcutKeys();
  const[productsMap, setProductsMap] = useState<any>()


  const skuNameToCodeMap = Object.fromEntries(
    Object.entries(skuProductDescriptionMap).map(([skuCode, skuName]) => [skuName, skuCode])
  );
  const displayedBatchIds = showAll ? batchIds : batchIds.slice(0, 4);

  const handleExpand = (batchId: string) => {
    setExpandedBatchId((prevBatchId) => (prevBatchId === batchId ? null : batchId));
  };

  const handleViewMore = () => {
    setShowAll(!showAll);
  };

  const convertFormDataToApiBody = () => {
    const skuDetails = Object.keys(formData.batchWiseData).map((batchId) => {
      const batchData = formData.batchWiseData[batchId];
      return {
        skuCode: formData.skuCode,
        batchId: batchId,
        pieceQty: Number(batchData.quantityPc),
        caseQty: Number(batchData.quantityCs),
        otherQty: Number(batchData.quantityOther),
        reason: batchData.reason,
        toStatus: batchData.updatedType,
      };
    });

    // Use selectedDistributor for company users, otherwise use current user's login ID
    const supplier = isCompanyUser && selectedDistributor 
      ? selectedDistributor 
      : JSON.parse(localStorage.authContext).user.loginId;

    return {
      supplier: supplier,
      warehouseId: null,
      outletCode: null,
      skuDetails: skuDetails,
    };
  };

  const handleSubmit = async () => {
    try {
      setApplyLoading(true)
      const apiBody = convertFormDataToApiBody();
      // logAnalyticsEvent( "stock_type_change_initiated", {
      //   "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
      //   "sku_code":apiBody.skuCode,
      //   "old_stock_type":apiBody.fromStatus,
      //   "new_stock_type":apiBody.skuDetails[0].toStatus
      // });
      
      const res=await ChannelkartNetworkPut(
        `${EnvironmentRegistry.getStockBaseUrl()}/consolidatedStock/typeTransfer`,
        apiBody
      );

      if(res.status!=200 && res.status!=201 && res.status!=204){
        throw new Error('Bad Request')
      }

      const updatedGenericModalProps = getGenericModalProps(
        "success",
        "New Stock type updated for selected SKU",
        "Continue",
        "",
        onSuccessCalback
      );
      setGenericModalProps(updatedGenericModalProps);
    } catch (error) {
      setGenericModalProps(getGenericModalProps("error", "Something Went Wrong!", "Continue", "", onSuccessCalback));
    } finally {
      setApplyLoading(false)
    }
  };

  const handleSkuCodeChange = (event: React.ChangeEvent<{}>, value: string | null) => {
    setFormData({
      ...formData,
      skuCode: value || "",
      skuName: value ? skuProductDescriptionMap[value] : "",
    });
  };

  const handleSkuNameChange = (event: React.ChangeEvent<{}>, value: string | null) => {
    setFormData({
      ...formData,
      skuName: value || "",
      skuCode: value ? skuNameToCodeMap[value] : "",
    });
  };

  const filterAndTransformData = (stockType: string, skuCode: string) => {
    const quantityFields = stockTypeMapping[stockType] || ["", "", ""];

    return existingBatchIdsRef.current.reduce(
      (acc, item) => {
        // Apply filtering condition based on stockType and skuCode
        const matchesSkuCode = item.skuCode.toLowerCase() === skuCode.toLowerCase();
        const hasRelevantQuantity =
          item[quantityFields[0]] > 0 || item[quantityFields[1]] > 0 || item[quantityFields[2]] > 0;

        if (matchesSkuCode && hasRelevantQuantity) {
          acc.transformedData[item.batchId] = {
            quantityCs: item[quantityFields[1]]?.toString()??'0',
            quantityPc: item[quantityFields[0]]?.toString()??'0',
            quantityOther: item[quantityFields[2]]?.toString()??'0',
            newQuantityCs: "",
            newQuantityPc: "",
            newQuantityOther: "",
            reason: "",
            updatedType: stockType,
          };

          // Also sum the total quantities
          acc.totalQuantityCs += item[quantityFields[1]];
          acc.totalQuantityPc += item[quantityFields[0]];
          acc.totalQuantityOther += item[quantityFields[2]];
        }
        return acc;
      },
      {
        transformedData: {} as batchWiseDataType,
        totalQuantityCs: 0,
        totalQuantityPc: 0,
        totalQuantityOther: 0,
      }
    );
  };

  // Main function to set current stock type IDs and compute total quantities
  const setCurrectStockTypeIds = async () => {
    if (!formData.skuCode) {
      setAllBatchIds({});
      setBatchIds([]);
      setFormData((prevFormData) => ({
        ...prevFormData,
        batchWiseData: {},
        quantityCs: "0",
        quantityPc: "0",
        quantityOther: "0",
      }));
      return;
    }

    // First fetch fresh batch data for the selected SKU
    await fetchBatchDataForSku(formData.skuCode);

    const stockType = formData.stockType.toLowerCase();
    const { transformedData, totalQuantityCs, totalQuantityPc, totalQuantityOther } =
      filterAndTransformData(stockType, formData.skuCode);

    // Set the transformed data to state
    setAllBatchIds(transformedData);
    setBatchIds(Object.keys(transformedData));

    // Update formData with total quantities
    setFormData((prevFormData) => ({
      ...prevFormData,
      batchWiseData: {},
      quantityCs: totalQuantityCs.toString(),
      quantityPc: totalQuantityPc.toString(),
      quantityOther: totalQuantityOther.toString(),
    }));
  };

  useEffect(() => {
    if (formData.skuCode) {
      setCurrectStockTypeIds();
    }
  }, [formData.stockType, formData.skuCode]);

  // A generalized handler for form field changes (e.g., stock type, reason)
  const handleFormChange = (
    event: React.ChangeEvent<{}>,
    value: string | null,
    field: keyof StockTypeUpdateFormData
  ) => {
    setFormData({
      ...formData,
      [field]: value || "",
    });
  };

  const handleSeachBatchId = (seachValue: string) => {
    const filteredBatchIds = Object.keys(allBatchIds).filter((id: string) =>
      id.toLowerCase().includes(seachValue.toLowerCase())
    );
    setBatchIds(filteredBatchIds);
  };

  useEffect(()=>handleSeachBatchId(searchValue), [searchValue])



  // Function to validate if all mandatory fields are filled and valid
  useEffect(() => {
    const validateForm = () => {
      const isFormValid =
        formData.skuCode &&
        formData.skuName &&
        formData.stockType &&
        Object.keys(formData.batchWiseData).length > 0;
      setIsApplyDisabled(!isFormValid);
    };
    validateForm();
  }, [formData]);

  const transformBatchData = (apiData: BatchWiseStockApiResponse[]): TransformedBatch[] => {
    const batchMap: { [key: string]: TransformedBatch } = {};
    apiData.forEach((item) => {
      const { batchId, skuCode, quantityType, ...rest } = item;
      const skuBatchKey = `${skuCode}-${batchId}`;
      if (!batchMap[skuBatchKey]) {
        batchMap[skuBatchKey] = {
          id: skuBatchKey,
          batchId: batchId,
          skuCode,
        };
      }
      
      // Construct proper QuantityDetail object with all required properties
      const quantityDetail = {
        committedQty: rest.committedQty ?? 0,
        availableQty: rest.availableQty ?? 0,
        unsellableQty: rest.unsellableQty ?? 0,
        inTransitQty: rest.inTransitQty ?? 0,
        onRouteQty: rest.onRouteQty ?? 0,
        returnedQty: rest.returnedQty ?? 0,
        lastModifiedTime: rest.lastModifiedTime ?? "",
        expiryDate: rest.expiryDate
      };
      
      // Use proper type assertion to handle the dynamic property assignment
      (batchMap[skuBatchKey] as any)[quantityType] = quantityDetail;
    });
    return Object.values(batchMap);
  };

  const fetchBatchDataForSku = async (skuCode: string) => {
    if (!skuCode) {
      existingBatchIdsRef.current = [];
      return;
    }

    try {
      // Get user context
      const authContext = JSON.parse(localStorage.authContext);
      const currentUser = authContext.user;
      
      // Determine supplier - use selected distributor for company users, or current user's loginId
      const supplier = isCompanyUser && selectedDistributor 
        ? selectedDistributor 
        : currentUser.loginId;

      const response = await ChannelkartNetworkGet(
        `${EnvironmentRegistry.getStockBaseUrl()}/consolidatedStock/getGroupedBatchWiseStockInfo?supplier=${supplier}&minimalResponse=true&skuCode=${skuCode}`
      );
      
      const content = response.data.content ?? [];
      const transformedData = transformBatchData(content);
      
      existingBatchIdsRef.current = transformToBatchwiseRowsData(transformedData);
    } catch (error) {
      console.error("Error fetching batch data for SKU:", error);
      existingBatchIdsRef.current = [];
    }
  };

  const onSuccessCalback = () => {
    setOpenNewStockTypeUpdateForm(false);
  }
  useEffect(() => {
  const fetchProducts = async () => {
    const productsData = await getDataFromLocalDb("Product");
    const productsMap: Record<string, Product> = productsData?.data?.[0]?.data ?? {};
    setProductsMap(productsMap)
  };

  fetchProducts();
}, []);

  useEffect(() => {
  const handleKeyDown = (event: KeyboardEvent) => {
    // Check for Ctrl+A or Command+A
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
      event.preventDefault(); // Prevent default select-all behavior
      // Only trigger if form is valid, not loading, and no modal is open
      if (!isApplyDisabled && !applyLoading && !document.querySelector('[role="dialog"]')) {
        console.log("Executing handleSubmit for Ctrl+A");
        handleSubmit();
      } else {
        console.log("Ignoring Ctrl+A: ", {
          isFormValid: !isApplyDisabled,
          isLoading: applyLoading,
          isModalOpen: !!document.querySelector('[role="dialog"]'),
        });
      }
    }
  };

  window.addEventListener("keydown", handleKeyDown);
  return () => {
    window.removeEventListener("keydown", handleKeyDown);
  };
}, [isApplyDisabled, applyLoading]);

  

  return (
    <>
      <div>
        <ScreenOverlay apply = {applyLoading} />
        <FormHeader
          parentLabel={"Stock Type Update"}
          label={"New Stock Type Update"}
          goBackToParent={() => setOpenNewStockTypeUpdateForm(false)}
        />
        <form className="CreateBatchIdForm">
          <div className="FormTextField">
            <div className="FormTextField_label">
              SKU Code
              <span style={{ color: "red" }}>*</span>
            </div>
            <Autocomplete
              options={Object.keys(skuProductDescriptionMap)}
              getOptionLabel={(option) => option}
              value={formData.skuCode}
              onChange={handleSkuCodeChange}
              sx={{ width: "-webkit-fill-available" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="skuCode"
                  variant="outlined"
                  size="small"
                  sx={customMuiInputStyle}
                  required
                />
              )}
            />
          </div>
          <div className="FormTextField">
            <div className="FormTextField_label">
              SKU Name
              <span style={{ color: "red" }}>*</span>
            </div>
            <Autocomplete
              options={Object.keys(skuNameToCodeMap)}
              getOptionLabel={(option) => option}
              value={formData.skuName}
              onChange={handleSkuNameChange}
              sx={{ width: "-webkit-fill-available" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="skuName"
                  variant="outlined"
                  size="small"
                  sx={customMuiInputStyle}
                  required
                />
              )}
            />
          </div>
          <div className="FormTextField">
            <div className="FormTextField_label">
              Current Stock Type
              <span style={{ color: "red" }}>*</span>
            </div>
            <Autocomplete
              disabled
              options={currentStockTypeOptions}
              getOptionLabel={(option) => option}
              value={formData.stockType}
              onChange={(event, value) =>
                handleFormChange(event, value, "stockType")
              }
              sx={{ width: "-webkit-fill-available" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="stockType"
                  variant="outlined"
                  size="small"
                  sx={customMuiInputStyle}
                  required
                  placeholder="Select a type"
                />
              )}
            />
          </div>
          {formData.stockType && formData.skuCode && formData.skuName && (
            <div className="FormTextField">
              <div className="edit_batch_label">Edit Batch Wise Qty.</div>
              <div className="batch_ids_container">
                <Box display="flex" justifyContent="space-between">
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                      color: "#28A745",
                      fontWeight: 600,
                      fontSize: "14px",
                      gap: "5px",
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: "14px !important" }} />
                    {batchIds.length} Batch IDs Available
                  </Box>
                  <div className="searchWidth">
                    <Search
                      label="Search Batch ID"
                      searchFunction={(value)=>setSearchValue(value)}
                    />
                  </div>
                </Box>
                <div className="batch_ids_block">
                  {displayedBatchIds.length > 0 ? (
                    displayedBatchIds.map((batchId) => {
                      return (
                        <BatchComponent
                          key={batchId}
                          showComponentV2={true}
                          batchNumber={batchId}
                          currentCs={allBatchIds[batchId].quantityCs}
                          currentOther={allBatchIds[batchId].quantityOther}
                          currentPc={allBatchIds[batchId].quantityPc}
                          formData={formData}
                          setFormData={setFormData}
                          onExpand={handleExpand}
                          expandedBatchId={expandedBatchId}
                          mandatoryFieldError={mandatoryFieldError}
                          setMandatoryFieldError={setMandatoryFieldError}
                          uom={skuUomMap[formData.skuCode]}
                          otherUnitName={productsMap[formData.skuCode].otherUnitName}
                        />
                      ); //isExpanded={expandedBatchId === batchId}
                    })
                  ) : (
                    <span className="no_batch_id_found_text">
                      No Batch IDs Found
                    </span>
                  )}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <span
                      className={`${
                        batchIds.length > 4
                          ? "view_more"
                          : "view_more hide_view"
                      }`}
                      onClick={handleViewMore}
                    >
                      {showAll
                        ? "View less"
                        : `View ${Object.keys(allBatchIds).length - 4} more`}
                    </span>
                  </Box>
                </div>
                {/* <Box
                  display="flex"
                  alignItems="center"
                  gap={"3px"}
                  marginTop={"5px"}
                  sx={{
                    backgroundColor: "#FFF9EF",
                    borderRadius: "10px",
                    width: "fit-content",
                    padding: "3px 9px",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faExclamationCircle}
                    color="#d09629"
                    fontSize="12px"
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "12px",
                      color: "#d09629",
                      fontWeight: "500",
                    }}
                  >
                    Please navigate to the Batch & Expiry screen to add a new
                    batch
                  </Typography>
                </Box> */}
              </div>
            </div>
          )}
          <Box
            mt={6}
            display="flex"
            justifyContent="right"
            width={"95%"}
            margin={"0"}
          >
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={isApplyDisabled || applyLoading}
              sx={{
                backgroundColor: isApplyDisabled ? "#E0E0E0" : "primary.main",
                color: isApplyDisabled ? "#A0A0A0" : "#FFFFFF",
              }}
            >
               {applyLoading && <><CircularProgress style={{...customStyle, color: "#FFFFFF", height: 17, width: 17}} />&nbsp; &nbsp;</>}
               APPLY
               &nbsp;<span style={{fontSize:"9.5px", textTransform:"none"}}>{positiveCta}</span>
            </Button>
          </Box>
        </form>
      </div>
      <CustomGenericModal
        type={genericModalProps?.type}
        message={genericModalProps?.message}
        leftButtonText={genericModalProps?.leftButtonText}
        rightButtonText={genericModalProps?.rightButtonText}
        modalOpen={genericModalProps?.open}
        onLeftButtonClick={genericModalProps?.onLeftButtonClick}
        onRightButtonClick={genericModalProps?.onRightButtonClick}
        onClose={() => {
          setGenericModalProps(initialGenericModalProps)
          onSuccessCalback()
       } }
      />
      <ConfirmationPopUp
        message={
          "Are you sure you want to update the stock type for the selected batch IDs?"
        }
        openConfirmModal={confirmPopupState}
        successMethod={handleSubmit}
        setOpenConfirmModal={setConfirmPopupState}
      />
      <GenericPopUp
        message={genericMessage}
        type={popupType}
        openGenericModal={openGenericModal}
        setOpenGenericModal={setOpenGenericModal}
      />
    </>
  );
};

export default CreateNewStockTypeUpdateForm;
